import * as types from "./actionTypes";
import { createEmitter } from "../sound/emitter";
import { createListener } from "../sound/listener";
import { MAX_POSITION, UNSUPPORTED_SAMPLE_RATE, encodeMessage } from "../sound/codec";
import { selectQueueSongs } from "../selectors";
import { sessionSongChanged } from "./sessionActions";

let emitter = null;
let listener = null;
// Every enable and disable advances this; work from an older attempt (a slow microphone
// prompt, a late module load, a message decoded by a stopped engine) is dropped.
let generation = 0;

export const soundSyncSet = (changes) => ({ type: types.SOUND_SYNC_SET, changes });

export const isSoundSyncSupported = () =>
  typeof window !== "undefined" && Boolean(window.AudioContext || window.webkitAudioContext);

const roleOf = (session) => (session.isHost ? "host" : "participant");

const stopAll = () => {
  generation += 1;
  if (emitter) {
    emitter.close();
    emitter = null;
  }
  if (listener) {
    listener.stop();
    listener = null;
  }
};

/**
 * The message for the session's current song and verse: positions in the queue, 1-based.
 * Null when the song sits beyond what a message can carry.
 */
export const currentSoundMessage = (state) => {
  const songs = selectQueueSongs(state);
  const index = songs.findIndex((song) => song.Id === state.session.SongId);
  const songPosition = index >= 0 ? index + 1 : 0;
  const versePosition = state.session.VerseNum + 1;
  if (state.session.SongId != null && index < 0) return null;
  if (!Number.isInteger(versePosition) || versePosition < 0 || versePosition > MAX_POSITION || songPosition > MAX_POSITION) return null;
  return encodeMessage({
    sessionCode: state.session.Code,
    songPosition,
    versePosition: songPosition === 0 ? 0 : versePosition,
  });
};

/** Hosts: play the current selection (and keep repeating it). */
export const soundSyncBroadcast = () => (dispatch, getState) => {
  if (!emitter || getState().soundSync.status !== "broadcasting") return;
  const message = currentSoundMessage(getState());
  if (!message) {
    // sending a clamped position would select a different song on the other phones
    emitter.stop(); // do not keep advertising a selection the host has already left
    dispatch(soundSyncSet({ error: `Songs or verses beyond position ${MAX_POSITION}, or a song missing from the queue, cannot be sent by sound.` }));
    return;
  }
  if (getState().soundSync.error) dispatch(soundSyncSet({ error: null }));
  emitter.broadcast(message);
};

/**
 * Participants: a message was heard; follow it when it names a song and a verse this device
 * has. A position beyond this device's queue, or a verse the song does not have, means the
 * two copies of the queue differ: the display is left as it is rather than changed to
 * something wrong.
 */
export const soundHeard = ({ songPosition, versePosition }) => (dispatch, getState) => {
  const state = getState();
  if (!state.soundSync.enabled || state.soundSync.role !== "participant" || state.soundSync.status !== "listening") return;
  if (![songPosition, versePosition].every((position) => Number.isInteger(position) && position >= 0 && position <= MAX_POSITION)) return;
  const songs = selectQueueSongs(state);
  if (songPosition > songs.length) return;
  const song = songPosition > 0 ? songs[songPosition - 1] : null;
  const verseNum = versePosition > 0 ? versePosition - 1 : -1;
  if (!song && verseNum >= 0) return;
  if (song && verseNum >= 0 && !(song.Verses || []).some((verse) => verse.Order === verseNum)) return;
  const songId = song ? song.Id : null;
  dispatch(soundSyncSet({ lastHeard: Date.now() }));
  if (state.session.SongId === songId && state.session.VerseNum === verseNum) return;
  dispatch(sessionSongChanged(songId, verseNum));
};

const failureStatus = (error) =>
  error?.name === "NotAllowedError" || error?.name === "SecurityError"
    ? "denied"
    : error?.name === "NotFoundError" || error?.name === UNSUPPORTED_SAMPLE_RATE
      ? "unsupported"
      : "error";

const activeStatus = (role) => (role === "host" ? "broadcasting" : "listening");

/** The audio context's state, as reported by the engine, becomes the sync status. */
const contextStateChanged = (state) => (dispatch, getState) => {
  const sync = getState().soundSync;
  if (!sync.enabled) return;
  if (state === "running") {
    if (sync.status === "interrupted") dispatch(soundSyncSet({ status: activeStatus(sync.role) }));
    return;
  }
  if (emitter) emitter.stop();
  dispatch(soundSyncSet({ status: "interrupted" }));
};

/**
 * Turns sound sync on: hosts start broadcasting, everyone else starts listening (which asks
 * for the microphone). Must be called from a user gesture so the browser allows audio.
 */
export const enableSoundSync = () => async (dispatch, getState) => {
  const { session } = getState();
  stopAll();
  const attempt = generation;
  const current = () => {
    const active = getState().session;
    return attempt === generation && active.Code === session.Code && roleOf(active) === roleOf(session) && !active.ended && !active.notFound;
  };

  if (!session.Code || session.ended || session.notFound) {
    dispatch(disableSoundSync());
    return false;
  }

  if (!isSoundSyncSupported()) {
    dispatch(soundSyncSet({ enabled: false, status: "unsupported", role: null, error: null }));
    return false;
  }

  const role = roleOf(session);
  dispatch(soundSyncSet({ enabled: false, status: "starting", error: null, role, sessionCode: session.Code, signal: 0, lastHeard: null }));
  if (!current()) return false;
  const onContextState = (state) => {
    if (current()) dispatch(contextStateChanged(state));
  };

  try {
    if (role === "host") {
      const engine = createEmitter({ onContextState });
      emitter = engine; // startup failures and disable must be able to release this context
      await engine.start();
      if (!current()) {
        engine.close();
        return false;
      }
      // the store subscriber (attachSoundSync) broadcasts as soon as it sees the host enabled
      dispatch(soundSyncSet({ enabled: true, status: "broadcasting", enabledAt: Date.now() }));
    } else {
      const engine = createListener({
        sessionCode: session.Code,
        onMessage: (message) => {
          if (current()) dispatch(soundHeard(message));
        },
        onSignal: (signal) => {
          if (current()) dispatch(soundSyncSet({ signal }));
        },
        onContextState,
      });
      listener = engine; // known before the wait, so a disable meanwhile stops it
      await engine.start();
      if (!current()) return false; // stopped meanwhile; the engine cleaned up after itself
      dispatch(soundSyncSet({ enabled: true, status: "listening", enabledAt: Date.now() }));
    }
    return true;
  } catch (error) {
    if (!current()) return false;
    stopAll();
    dispatch(soundSyncSet({ enabled: false, status: failureStatus(error), role: null, error: error?.message || null }));
    return false;
  }
};

export const disableSoundSync = () => (dispatch) => {
  stopAll();
  dispatch(soundSyncSet({ enabled: false, status: "off", role: null, sessionCode: null, signal: 0, error: null, lastHeard: null, enabledAt: null }));
};

/**
 * After an interruption (a call, the app in the background): tries to get the audio going
 * again. Hosts then broadcast their selection again, since messages were missed meanwhile.
 */
export const resumeSoundSync = () => async (dispatch, getState) => {
  const sync = getState().soundSync;
  if (!sync.enabled) return false;
  const engine = emitter || listener;
  if (!engine) return false;
  if (engine.needsRestart) return dispatch(enableSoundSync());
  const attempt = generation;
  const current = () => attempt === generation && (emitter || listener) === engine && getState().soundSync.enabled;
  try {
    const running = await engine.resume();
    if (!current()) return false;
    dispatch(soundSyncSet({ status: running ? activeStatus(sync.role) : "interrupted" }));
    if (running && sync.role === "host" && sync.status === "broadcasting") dispatch(soundSyncBroadcast());
    return running;
  } catch {
    if (!current()) return false;
    dispatch(soundSyncSet({ status: "interrupted" }));
    return false;
  }
};

/**
 * Keeps a running engine honest: it is turned off when the device's role or session changes
 * under it, and a host re-broadcasts whenever the selection or the queue order changes
 * (the message carries queue positions, so a reorder changes it too). Returns the
 * unsubscribe function.
 */
export function attachSoundSync(store) {
  let last = null;
  let lastSongs = null;
  let orderKey = "";
  return store.subscribe(() => {
    const state = store.getState();
    const sync = state.soundSync;
    if (!sync.enabled && sync.status !== "starting") {
      last = null;
      return;
    }
    if (!state.session.Code || state.session.ended || state.session.notFound || roleOf(state.session) !== sync.role || state.session.Code !== sync.sessionCode) {
      store.dispatch(disableSoundSync());
      return;
    }
    if (!sync.enabled || sync.role !== "host" || sync.status !== "broadcasting") {
      last = null;
      return;
    }
    if (state.songs !== lastSongs) {
      lastSongs = state.songs;
      orderKey = selectQueueSongs(state)
        .map((song) => song.Id)
        .join(",");
    }
    const key = `${state.session.SongId}:${state.session.VerseNum}:${orderKey}`;
    if (key === last) return;
    last = key;
    store.dispatch(soundSyncBroadcast());
  });
}
